import {Card, Checkbox, Col, Collapse, Form, Row, Select, Space, Tag, Typography} from 'antd';
import {ProForm, ProFormSelect, ProFormText} from '@ant-design/pro-components';
import {ProFormDateRangePicker} from '@ant-design/pro-form/lib/components';
import {useAtom} from 'jotai';
import DynamicSelect from '../../../DigitalAssets/Components/DynamicSelect';
import {RESET} from 'jotai/utils';
import {useContext, useEffect, useMemo} from 'react';
import {DDEXPageContext} from './DDEXMainPage';
import {atomWithHash} from "jotai-location";

const {Option} = Select;
const {Text} = Typography;

type Searchcriteria = {
    upc: string;
    podio_id: string;
    isrc: string;
    name: string;
    original_release_date: Date;
    digital_release_date: Date;
    genre: string[];
    label: string[];
    asset_label: string[];
    artist: string[];
    territories: string[];
    type: ('Album' | 'Single' | 'Ep')[];
    uploaded_to: ('Youtube' | 'Believe' | 'Spotify')[];
    page: number;
    pageSize: number;
    sorter_field?: string;
    sorter_order?: string;
};

type FormValues = {
    UPC: string;
    ISRC: string;
    Title: string;
    Artist: string[];
    Label: string[];
    AssetLabel: string;
    ReleaseDate: [string, string];
    ReleaseType: string;
    Genre: string;
    Territories: string[];
    uploaded_to: string[];
};

<Select maxLength={10} maxTagCount={10} maxTagPlaceholder={10} children={ProFormSelect}></Select>;

export const searchCriteriaAtom = atomWithHash<Partial<Searchcriteria>>('releasesQuery', {page: 1, pageSize: 10});

const SearchForm = () => {
    const [searchCriteria, setSearchCriteria] = useAtom(searchCriteriaAtom);
    const {Panel} = Collapse;
    const {releasesActionRef} = useContext(DDEXPageContext);
    const [form] = Form.useForm();

    useEffect(() => {
        form.setFieldsValue(searchCriteria);
    }, [searchCriteria, form]);

    const handleSubmit = async <T, >(values: T) => {
        releasesActionRef.current.setPageInfo({current: 1});
        setSearchCriteria({...values, page: 1, pageSize: 10});
    };

    // Function to remove a specific criteria from search
    const handleRemoveCriterion = (key: string, value?: string | number) => {
        const updatedCriteria = { ...searchCriteria };
        console.log({oldCriteria: { ...searchCriteria }});

        // Handle array type criteria (need to remove a specific value)
        if (value !== undefined && Array.isArray(updatedCriteria[key as keyof typeof updatedCriteria])) {
            const currentArray = updatedCriteria[key as keyof typeof updatedCriteria] as any[];
            updatedCriteria[key] = currentArray.filter(
                (item) => item !== value
            ) as any;

            // If array becomes empty, remove the property
            if ((updatedCriteria[key as keyof typeof updatedCriteria] as any[]).length === 0) {
                updatedCriteria[key] = undefined;
            }
        } else {
            // For simple string/date criteria just delete the property
            updatedCriteria[key] = undefined;
        }

        // Keep the pagination properties
        const newCriteria = { ...updatedCriteria, page: 1, pageSize: 10 }
        console.log({newCriteria});
        setSearchCriteria(newCriteria);
        form.setFieldsValue(updatedCriteria); // Update form values

        // Trigger the search with updated criteria
        handleSubmit<Searchcriteria>(newCriteria as Searchcriteria);
    };

    // Generate tags for active search criteria
    const renderActiveCriteriaTags = useMemo(() => {
        if (!searchCriteria) return null;

        const tags = [];

        // Simple string fields
        if (searchCriteria.upc) {
            tags.push(
                <Tag
                    key="upc"
                    closable
                    // color="blue"
                    onClose={() => handleRemoveCriterion('upc')}
                >
                    UPC: {searchCriteria.upc}
                </Tag>
            );
        }

        if (searchCriteria.podio_id) {
            tags.push(
                <Tag
                    key="podio_id"
                    closable
                    onClose={() => handleRemoveCriterion('podio_id')}
                >
                    Podio ID: {searchCriteria.podio_id}
                </Tag>
            );
        }

        if (searchCriteria.name) {
            tags.push(
                <Tag
                    key="name"
                    closable
                    // color="blue"
                    onClose={() => handleRemoveCriterion('name')}
                >
                    Title: {searchCriteria.name}
                </Tag>
            );
        }

        if (searchCriteria.isrc) {
            tags.push(
                <Tag
                    key="isrc"
                    closable
                    // color="blue"
                    onClose={() => handleRemoveCriterion('isrc')}
                >
                    ISRC: {searchCriteria.isrc}
                </Tag>
            );
        }

        // Array fields
        if (searchCriteria.artist?.length) {
            searchCriteria.artist.forEach((artist, index) => {
                tags.push(
                    <Tag
                        key={`artist-${artist}`}
                        closable
                        // color="green"
                        onClose={() => handleRemoveCriterion('artist', artist)}
                    >
                        Artist: {artist}
                    </Tag>
                );
            });
        }

        if (searchCriteria.label?.length) {
            searchCriteria.label?.forEach((label, index) => {
                tags.push(
                    <Tag
                        key={`label-${label}`}
                        closable
                        // color="orange"
                        onClose={() => handleRemoveCriterion('label', label)}
                    >
                        Label: {label}
                    </Tag>
                );
            });
        }

        if (searchCriteria.asset_label?.length) {
            searchCriteria.asset_label?.forEach((asset_label, index) => {
                tags.push(
                    <Tag
                        key={`asset_label-${asset_label}`}
                        closable
                        // color="orange"
                        onClose={() => handleRemoveCriterion('asset_label', asset_label)}
                    >
                        Asset Label: {asset_label}
                    </Tag>
                );
            })
        }

        if (searchCriteria.territories?.length) {
            searchCriteria.territories.forEach((territory, index) => {
                tags.push(
                    <Tag
                        key={`territory-${territory}`}
                        closable
                        // color="purple"
                        onClose={() => handleRemoveCriterion('territories', territory)}
                    >
                        Territory: {territory}
                    </Tag>
                );
            });
        }

        if (searchCriteria.genre?.length) {
            searchCriteria.genre?.forEach((genre, index) => {
                tags.push(
                    <Tag
                        key={`genre-${genre}`}
                        closable
                        // color="cyan"
                        onClose={() => handleRemoveCriterion('genre', genre)}
                    >
                        Genre: {genre}
                    </Tag>
                );
            })
        }

        // Date range
        if (searchCriteria.original_release_date) {
            const dateStr = searchCriteria.original_release_date instanceof Date
                ? searchCriteria.original_release_date.toLocaleDateString()
                : String(searchCriteria.original_release_date);

            tags.push(
                <Tag
                    key="original_release_date"
                    closable
                    // color="gold"
                    onClose={() => handleRemoveCriterion('original_release_date')}
                >
                    Release date: {dateStr}
                </Tag>
            );
        }

        // Checkbox groups
        if (searchCriteria.type?.length) {
            searchCriteria.type.forEach((type, index) => {
                tags.push(
                    <Tag
                        key={`type-${type}`}
                        closable
                        // color="magenta"
                        onClose={() => handleRemoveCriterion('type', type)}
                    >
                        Type: {type}
                    </Tag>
                );
            });
        }

        if (searchCriteria.uploaded_to?.length) {
            searchCriteria.uploaded_to.forEach((platform, index) => {
                tags.push(
                    <Tag
                        key={`platform-${platform}`}
                        closable
                        // color="geekblue"
                        onClose={() => handleRemoveCriterion('uploaded_to', platform)}
                    >
                        Platform: {platform}
                    </Tag>
                );
            });
        }

        return tags.length > 0 ? (
            <div style={{ marginTop: 16 }}>
                {tags}
            </div>
        ) : null;
    }, [searchCriteria]);

    // Custom panel header with tags
    const panelHeader = (
        <div>
            <div>
                <Text strong>Search Form</Text>
            </div>
            {renderActiveCriteriaTags}
        </div>
    );

    return (
        <Card
            style={{
                marginBottom: '4px'
            }}>
            <Collapse ghost>
                <Panel header={panelHeader} key='1'>
                    <ProForm<Searchcriteria>
                        form={form}
                        submitter={{
                            searchConfig: {
                                submitText: 'Search',
                                resetText: 'Reset'
                            }
                        }}
                        initialValues={searchCriteria}
                        onFinish={handleSubmit<Searchcriteria>}
                        onReset={() => {
                            releasesActionRef.current.setPageInfo({current: 1});
                            form.resetFields();
                            setSearchCriteria(RESET);
                        }}>
                        <ProForm.Group>
                            <ProFormText name='upc' label='UPC' placeholder='Search by UPC (comma separated)' width={300}/>
                            <ProFormText name='podio_id' label='Podio ID' placeholder='Search by Podio ID (comma separated)' width={300}/>
                            <ProFormText
                                name='name'
                                label='Release Title'
                                placeholder='Search by Release Title'
                                width={300}
                            />
                            <ProFormText
                                name='isrc'
                                label='ISRC'
                                placeholder='Search by ISRC'
                                convertValue={(value: string) => value?.replace(/-/g, '')}
                                width={300}
                            />
                        </ProForm.Group>
                        <ProForm.Group>
                            <DynamicSelect
                                mode={'multiple'}
                                name='artist'
                                label='Artist'
                                rules={[{required: false}]}
                                placeholder='Select Artist '
                                endpoint='/api/digitalassets/options?timestamp='
                                targetField='artist'
                                searchAtom={searchCriteriaAtom as any}
                            />
                            <DynamicSelect
                                mode={'multiple'}
                                name='label'
                                label='Label'
                                rules={[{required: false}]}
                                placeholder='Select Label '
                                endpoint='/api/digitalassets/label/options?timestamp='
                                targetField='label'
                                searchAtom={searchCriteriaAtom as any}
                            />
                            <DynamicSelect
                                mode={'multiple'}
                                name='asset_label'
                                label='Asset Label'
                                rules={[{required: false}]}
                                placeholder='Select Asset Label'
                                endpoint='/api/digitalassets/assetlabel/options?timestamp='
                                targetField='asset_label'
                                searchAtom={searchCriteriaAtom as any}
                            />
                        </ProForm.Group>
                        <ProForm.Group>
                            <DynamicSelect
                                mode={'multiple'}
                                name='territories'
                                label='Territories'
                                rules={[{required: false}]}
                                placeholder='Select Territories '
                                endpoint='/api/digitalassets/territories/options?timestamp='
                                targetField='territories'
                                searchAtom={searchCriteriaAtom as any}
                            />
                            <DynamicSelect
                                mode={'multiple'}
                                name='genre'
                                label='Genre'
                                rules={[{required: false}]}
                                placeholder='Select Genre '
                                endpoint='/api/digitalassets/genre/options?timestamp='
                                targetField='genre'
                                searchAtom={searchCriteriaAtom as any}
                            />
                        </ProForm.Group>
                        <ProForm.Group>
                            <ProFormDateRangePicker name='original_release_date' label='Release Date'/>
                            <Row>
                                <Form.Item name='uploaded_to' label='Platform'>
                                    <Checkbox.Group onChange={(value) => {
                                    }}>
                                        <Row>
                                            <Col span={14}>
                                                <Checkbox value='Spotify'>Spotify</Checkbox>
                                            </Col>
                                            <Col span={14}>
                                                <Checkbox value='YouTube'>YouTube</Checkbox>
                                            </Col>
                                            <Col span={14}>
                                                <Checkbox value='Believe'>Believe</Checkbox>
                                            </Col>
                                        </Row>
                                    </Checkbox.Group>
                                </Form.Item>
                                <Form.Item name='type' label='Release Type'>
                                    <Checkbox.Group
                                        onChange={(value) => {
                                            // value = searchCriteria.uploaded_to;
                                        }}>
                                        <Row>
                                            <Col span={14}>
                                                <Checkbox value='Album'>Album</Checkbox>
                                            </Col>
                                            <Col span={14}>
                                                <Checkbox value='Ep'>Ep</Checkbox>
                                            </Col>
                                            <Col span={14}>
                                                <Checkbox value='Single'>Single</Checkbox>
                                            </Col>
                                        </Row>
                                    </Checkbox.Group>
                                </Form.Item>
                            </Row>
                        </ProForm.Group>

                        <Form.Item wrapperCol={{span: 12, offset: 6}}>
                            <Space></Space>
                        </Form.Item>
                    </ProForm>
                </Panel>
            </Collapse>
        </Card>
    );
};
export default SearchForm;
